import {
  InfoBox,
  PaginationBar,
  Tooltip,
  Option,
  useDevice,
  useWindowSize,
  colors,
  icons,
  getTestAttributes,
  noop,
  randomUUID,
  titleizeString,
} from '@chillibean/component-library';
import { Table } from '@chillibean/component-library/legacy';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { useFilterParams } from '../../hooks';
import { setActivitiesCurrentPage, setActivitiesPerPage } from '../../redux/actions';
import { MoreDataModal } from '../modals';

export function ActivitiesTable(props) {
  const { clearFilters = noop } = props;

  const DEFAULT_MORE_DETAILS_MODAL = {
    data: [],
    visible: false,
  };
  const BASE_COLUMN_WIDTH = 200;
  const EXTRA_PADDING = 60;
  const [moreDetailsModal, setMoreDetailsModal] = useState(DEFAULT_MORE_DETAILS_MODAL);
  const dispatch = useDispatch();
  const {
    activities: { data: activities, loading },
    activitiesCurrentPage: { data: currentPage },
    activitiesLength: { data: activitiesLength },
    activitiesPerPage: { data: perPage },
  } = useSelector((state) => state.activitiesReducer);
  const { account } = useSelector((state) => state.internalReducer.internal);
  const { sitesEnabled } = account || { sitesEnabled: false };
  const { updateSearchParam } = useFilterParams();
  const { mobile } = useDevice();
  const {
    filteringChips: { data: chips },
  } = useSelector((state) => state.filteringReducer);
  const tableContainerRef = useRef(null);
  const changeCellRef = useRef(null);
  const [detailsWidth, setDetailsWidth] = useState(BASE_COLUMN_WIDTH * 1.5 - EXTRA_PADDING);
  const pageCount = Math.ceil(activitiesLength / perPage);

  const resizeListener = () => {
    if (!changeCellRef.current) return setDetailsWidth(BASE_COLUMN_WIDTH * 1.5 - EXTRA_PADDING);

    return setDetailsWidth(changeCellRef.current.offsetWidth - EXTRA_PADDING);
  };

  useEffect(() => {
    resizeListener();
  }, [changeCellRef?.current]);

  useWindowSize({ resizeFunction: resizeListener });

  const handleMoreDetails = (activity) => {
    setMoreDetailsModal({
      data: [
        {
          content: activity.formattedDetails({ sitesEnabled: sitesEnabled }),
          title: 'Activity Info',
          withSeparator: false,
        },
        {
          content: activity.moreDetails,
          title: 'More Details',
          withSeparator: true,
        },
      ],
      visible: true,
    });
  };

  const prepareTableHeaders = useMemo(() => {
    const tableHeadersOne = [
      { Header: 'EVENT DATE', accessor: 'Event Date' },
      { Header: 'PERFORMER', accessor: 'Performer', overflowVisible: true },
      { Header: 'LOCATION', accessor: 'Location' },
      { Header: 'ACTION', accessor: 'Action' },
      { Header: 'OBJECT', accessor: 'Object', width: BASE_COLUMN_WIDTH / 2 },
      { Header: 'ID', accessor: 'ID', width: BASE_COLUMN_WIDTH / 3 },
    ];

    if (sitesEnabled) {
      tableHeadersOne.push({ Header: 'SUBJECT', accessor: 'Subject' });
      tableHeadersOne.push({ Header: 'STUDY EVENT', accessor: 'Study Event' });
      tableHeadersOne.push({ Header: 'STUDY PROCEDURE', accessor: 'Study Procedure' });
    }

    const tableHeadersTwo = [{ Header: 'DETAILS', accessor: 'Details', width: BASE_COLUMN_WIDTH * 1.5 }];

    return [...tableHeadersOne, ...tableHeadersTwo];
  }, [sitesEnabled]);

  const buildPaginationString = () => {
    if (loading) return '';

    const start = perPage * (currentPage - 1) + 1;
    const maxCapacity = perPage * currentPage;
    const end = maxCapacity > activitiesLength ? activitiesLength : maxCapacity;
    const max = activitiesLength;

    return `${!mobile ? 'Results:' : ''} ${start} - ${end} of ${max}`;
  };

  const handlePageChange = (e, page) => {
    e.preventDefault();

    dispatch(setActivitiesCurrentPage({ data: page }));
    updateSearchParam('page', page);
    if (tableContainerRef.current) tableContainerRef.current.scrollTo({ behavior: 'smooth', top: 0 });
  };

  const getChangeCell = ({ activity }) => {
    const hasMoreDetails = !_.isEmpty(activity.moreDetails);
    let { change } = activity;
    if (typeof change === 'boolean') change = titleizeString(change.toString());

    return (
      <div label={change}>
        <ChangeCell ref={changeCellRef} label={change}>
          <ChangeText $maxWidth={detailsWidth}>{change}</ChangeText>
          {hasMoreDetails && (
            <Tooltip content="More Details" id={randomUUID()}>
              <IconWrapper onClick={() => handleMoreDetails(activity)} {...getTestAttributes('more-details-button')}>
                <Icon color="black" height={14} icon={icons.baselineInformation} width={14} />
              </IconWrapper>
            </Tooltip>
          )}
        </ChangeCell>
      </div>
    );
  };

  const getPerformerCell = ({ activity }) => {
    const performerRole = activity.performer?.role;
    const performerName = activity.performer?.fullName();
    const performerEmail = activity.performer?.email;

    const renderTooltipContent = () => {
      return (
        <InfoWrapper>
          <InfoBox containerPadding="0px" extraInfo={performerRole} header={performerName} subHeader={performerEmail} />
        </InfoWrapper>
      );
    };

    return (
      <div label={activity.performer?.fullNameAndEmail()}>
        <PerformerCell>
          <Tooltip backgroundColor="white" content={renderTooltipContent()} hasArrow={false} id={randomUUID()}>
            <PerformerText>{activity.performer?.fullNameAndEmail()}</PerformerText>
          </Tooltip>
        </PerformerCell>
      </div>
    );
  };

  const handleRowPerPageSelect = (e) => {
    dispatch(setActivitiesPerPage({ data: e.target.value }));
    dispatch(setActivitiesCurrentPage({ data: 1 }));
    updateSearchParam('per_page', e.target.value);
    updateSearchParam('page', 1);
  };

  const prepareTableRows = (tableData) => {
    return tableData?.map((activity) => {
      return activity.formattedDetails({
        change: getChangeCell({ activity }),
        performer: getPerformerCell({ activity }),
        sitesEnabled: sitesEnabled,
      });
    });
  };

  if (!loading && activitiesLength === 0) {
    return (
      <EmptyTableContainer>
        <InfoEmptyContainer>
          <ClearFilterText>
            {chips.length > 0
              ? 'No results found for these filters. Adjust or clear filters to try again.'
              : 'There is no activity to show yet.'}
          </ClearFilterText>
          <ResetFilterWrapper onClick={clearFilters}>Clear all filters</ResetFilterWrapper>
        </InfoEmptyContainer>
      </EmptyTableContainer>
    );
  }

  return (
    <>
      <MoreDataModal
        data={moreDetailsModal.data}
        onClose={() => setMoreDetailsModal(DEFAULT_MORE_DETAILS_MODAL)}
        visible={moreDetailsModal.visible}
      />
      <TableWrapper ref={tableContainerRef}>
        <Table headers={prepareTableHeaders} loading={loading} tableData={prepareTableRows(activities)} />
      </TableWrapper>
      <PaginationBar
        currentPage={currentPage}
        initialPage={1}
        loading={loading}
        onChange={handlePageChange}
        pageCount={pageCount}
        paginationText={buildPaginationString()}
        rowSelectOptions={{
          onChange: handleRowPerPageSelect,
          options: [new Option('25', 25), new Option('50', 50), new Option('100', 100)],
          value: perPage,
        }}
      />
    </>
  );
}

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const InfoWrapper = styled.div`
  color: black;
`;

const EmptyTableContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.blue.light};
  width: 100%;
  height: 100%;
  text-align: center;
`;

EmptyTableContainer.displayName = 'EmptyTableContainer';

const InfoEmptyContainer = styled.div``;

const ClearFilterText = styled.div``;

const ResetFilterWrapper = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

const ChangeCell = styled.div`
  display: flex;
  justify-content: space-between;
  padding-right: 24px;

  > svg {
    cursor: pointer;
  }
`;

const IconWrapper = styled.div`
  cursor: pointer;
  margin-left: 10px;
`;

const PerformerText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PerformerCell = styled.div`
  position: relative;
`;

const ChangeText = styled.div`
  max-width: ${(props) => props.$maxWidth}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

ActivitiesTable.propTypes = {
  clearFilters: PropTypes.func,
};

export default ActivitiesTable;
